<script setup>
import { computed } from 'vue'
import { Icon } from '@iconify/vue'

/**
 * AppButton — single source of truth for all button styles in CDP 2.0.
 *
 * Variants (from Figma node 23327:53807):
 *   primary   — vivid-blue → protoss-pylon gradient, white text
 *   default   — solid blue-500, white text
 *   secondary — blue-50 bg, blue-500 text
 *   tertiary  — gray-50 bg, blue-500 text
 *   outlined  — white bg, gray-200 border, gray-700 text
 *   link      — no bg/border; coloured=true → blue text, coloured=false → gray text
 *   dashed    — white bg, dashed gray-200 border, gray text
 *
 * ⚠️ Template must stay SINGLE-ROOT: no comments at the template root (before/
 * between the split `v-if` and standard `v-else`). A root-level comment makes
 * Vue render AppButton as a fragment in dev, so `$el` points at the comment node
 * and HeadlessUI trigger focus (`MenuButton`/`PopoverButton`) throws
 * "e3.focus is not a function" on close.
 *
 * Sizes:  sm (h-7, text-xs)  |  md (h-[34px], text-sm)
 * Icons:  leadingIcon  — any Iconify icon name on the left
 *         dropdown     — shows chevron-down on the right
 *         split        — adds a visual divider before the chevron (split-button pattern)
 *                        makes the chevron emit 'dropdown-click' separately from the main 'click'
 */
const props = defineProps({
  variant:      { type: String,  default: 'primary' },
  // 'primary' | 'default' | 'secondary' | 'tertiary' | 'outlined' | 'link' | 'dashed'

  size:         { type: String,  default: 'md' },     // 'sm' | 'md'
  disabled:     { type: Boolean, default: false },
  loading:      { type: Boolean, default: false },
  coloured:     { type: Boolean, default: true },      // link variant: blue (true) vs gray (false)
  leadingIcon:  { type: String,  default: null },      // Iconify icon name, e.g. 'heroicons:plus'
  trailingIcon: { type: String,  default: null },      // Iconify icon name on the right edge (e.g. 'heroicons:chevron-right-16-solid' for Next-style buttons). Mutually exclusive with `dropdown`; `dropdown` wins if both are set.
  dropdown:     { type: Boolean, default: false },     // show chevron-down
  open:         { type: Boolean, default: false },     // chevron rotates 180° when true (pass the panel's open state)
  split:        { type: Boolean, default: false },     // split button: divider + separate dropdown click
  splitDivider: { type: Boolean, default: true },      // show/hide the divider line between label and chevron
  as:           { type: [String, Object], default: 'button' },
  type:         { type: String,  default: 'button' },  // html button type
})

const emit = defineEmits(['click', 'dropdown-click'])

const isDisabled = computed(() => props.disabled || props.loading)
const isSplit    = computed(() => props.split && props.dropdown)

// ── Size ─────────────────────────────────────────────────────────────────────

const sizeClass = computed(() =>
  props.size === 'sm' ? 'h-7 px-2 py-1 text-xs' : 'h-[34px] p-2 text-sm'
)
const iconSize    = 16
const chevronSize = computed(() => props.size === 'sm' ? 16 : 18)

// ── Variant colours ───────────────────────────────────────────────────────────

const ACTIVE = {
  primary:    'bg-gradient-to-r from-vivid-blue to-protoss-pylon text-white hover:from-[#0015dc] hover:to-[#0087ce]',
  default:    'bg-tint-1 text-white hover:shadow-[0_0_5px_#69a0ff]',
  secondary:  'bg-blue-50 text-tint-1 hover:shadow-[0_0_5px_#69a0ff]',
  tertiary:   'bg-gray-50 text-tint-1 hover:shadow-[0_0_5px_#69a0ff]',
  outlined:   'bg-white border border-gray-200 text-gray-700 hover:border-blue-200 hover:bg-white hover:shadow-[0_0_5px_#69a0ff]',
  dashed:     'bg-white border border-dashed border-gray-200 text-gray-500 hover:bg-gray-50 hover:border-blue-200 hover:shadow-[0_0_5px_#69a0ff]',
  success:    'bg-green-500 text-white hover:bg-green-600',
  danger:     'bg-red-500 text-white hover:bg-red-600',
  // Tab-toggle variants (pill-style view switcher)
  'tab':      'text-gray-600 hover:bg-gray-100 rounded-[8px] w-20 cursor-pointer focus:!ring-0',
  'tab-active':'bg-blue-50 border border-tint-1 rounded-[6px] text-tint-1 font-semibold w-20 cursor-pointer focus:!ring-0',
}

const DISABLED = {
  primary:    'bg-gradient-to-r from-[rgba(32,53,255,0.5)] to-[rgba(1,168,255,0.5)] text-white',
  default:    'bg-gradient-to-r from-[rgba(32,53,255,0.5)] to-[rgba(1,168,255,0.5)] text-white',
  secondary:  'bg-blue-50 text-blue-400',
  tertiary:   'bg-gray-50 text-blue-400',
  outlined:   'bg-white border border-gray-50 text-gray-300',
  dashed:     'bg-white border border-dashed border-gray-50 text-gray-300',
  success:    'bg-green-200 text-white',
  danger:     'bg-red-200 text-white',
  'tab':      'text-gray-300 rounded-[8px] w-20',
  'tab-active':'bg-blue-50 border border-blue-200 text-blue-300 rounded-[8px] w-20',
}

const linkActive    = computed(() => props.coloured
  ? 'bg-white text-tint-1 hover:shadow-[0_0_5px_#e5e5e5]'
  : 'bg-white text-gray-500 hover:bg-gray-50'
)
const linkDisabled  = computed(() => props.coloured ? 'bg-white text-blue-100' : 'bg-white text-gray-300')

const variantClass = computed(() => {
  if (props.variant === 'link') return isDisabled.value ? linkDisabled.value : linkActive.value
  return isDisabled.value
    ? (DISABLED[props.variant] ?? DISABLED.outlined)
    : (ACTIVE[props.variant]   ?? ACTIVE.outlined)
})

// ── Divider colour between split sections ─────────────────────────────────────
const dividerClass = computed(() => {
  switch (props.variant) {
    case 'primary':
    case 'default':   return 'bg-white/30'
    case 'secondary':
    case 'tertiary':  return 'bg-blue-200'
    default:          return 'bg-gray-200'
  }
})

// ── Shared base (everything except bg/border which comes from variantClass) ───
const baseClass = 'inline-flex items-center justify-center gap-1 rounded-md font-medium transition duration-200 focus:outline-none focus:ring-2 focus:ring-blue-300 whitespace-nowrap'
const cursorClass = computed(() => isDisabled.value ? 'cursor-not-allowed pointer-events-none' : 'cursor-pointer')
</script>

<template>
  <div
    v-if="isSplit"
    class="inline-flex items-stretch rounded-md overflow-hidden"
    :class="[variantClass, cursorClass]"
  >
    <!-- Main action -->
    <button
      :type="type"
      :disabled="isDisabled"
      class="inline-flex items-center gap-1 font-medium transition duration-200 focus:outline-none cursor-pointer"
      :class="sizeClass"
      @click="emit('click', $event)"
    >
      <Icon
        v-if="leadingIcon && !loading"
        :icon="leadingIcon"
        :width="iconSize"
        :height="iconSize"
        class="shrink-0"
      />
      <Icon
        v-else-if="loading"
        icon="svg-spinners:3-dots-fade"
        :width="iconSize"
        :height="iconSize"
        class="shrink-0"
      />
      <slot />
    </button>

    <!-- Divider -->
    <span v-if="splitDivider" class="self-stretch w-px my-1" :class="dividerClass" />

    <!-- Dropdown trigger -->
    <button
      :type="type"
      :disabled="isDisabled"
      class="inline-flex items-center justify-center transition duration-200 focus:outline-none px-1.5 cursor-pointer"
      @click.stop="emit('dropdown-click', $event)"
    >
      <Icon
        icon="heroicons:chevron-down-16-solid"
        :width="chevronSize"
        :height="chevronSize"
        class="shrink-0 transition-transform duration-200"
        :class="{ 'rotate-180': open }"
      />
    </button>
  </div>

  <component
    :is="as"
    v-else
    :type="as === 'button' ? type : undefined"
    :disabled="as === 'button' ? isDisabled : undefined"
    :aria-disabled="isDisabled || undefined"
    class="inline-flex items-center justify-center gap-1 rounded-md font-medium transition duration-200 focus:outline-none focus:ring-2 focus:ring-blue-300 whitespace-nowrap"
    :class="[sizeClass, variantClass, cursorClass]"
    @click="!isDisabled && emit('click', $event)"
  >
    <Icon
      v-if="leadingIcon && !loading"
      :icon="leadingIcon"
      :width="iconSize"
      :height="iconSize"
      class="shrink-0"
    />
    <Icon
      v-else-if="loading"
      icon="svg-spinners:3-dots-fade"
      :width="iconSize"
      :height="iconSize"
      class="shrink-0"
    />
    <slot />
    <Icon
      v-if="dropdown"
      icon="heroicons:chevron-down-16-solid"
      :width="chevronSize"
      :height="chevronSize"
      class="shrink-0 transition-transform duration-200"
      :class="{ 'rotate-180': open }"
    />
    <Icon
      v-else-if="trailingIcon"
      :icon="trailingIcon"
      :width="iconSize"
      :height="iconSize"
      class="shrink-0"
    />
  </component>
</template>
